import _ from 'lodash';

import {useAllQueuesQuery} from '../../../generated/graphql';
import {useViewer} from '../../../state/viewer/hooks';
import {extractOrganizations, extractQueues} from './queueUtil';

/**
 * Return information about the queues in one or all entities.
 */
export const useQueues = (
  entityName: string | undefined,
  includeProjectQueues: boolean,
  includeGlobalQueues: boolean // Include queues that user would not naturally have access to by entity membership
) => {
  const viewer = useViewer();
  const personalEntity = viewer?.username ?? '';
  const queryAll = useAllQueuesQuery({pollInterval: 5000});
  const queues = queryAll.data
    ? extractQueues(
        queryAll,
        personalEntity,
        includeProjectQueues,
        includeGlobalQueues
      )
    : [];
  const organizations = queryAll.data ? extractOrganizations(queryAll) : {};

  // TODO: Instead of getting everything and filtering, only query for one entity.
  const filteredQueues = entityName
    ? queues.filter(q => q.entityName === entityName)
    : queues;
  return {
    loading: queryAll.loading,
    error: queryAll.error,
    refetch: queryAll.refetch,
    queues: filteredQueues,
    organizations,
  };
};

/**
 * Information about a single queue.
 *
 * TODO: This is inefficient. We are retrieving information about all queues
 *       in the entity to get information about one. The issue is that we only
 *       have an API currently to look up the queue by name, when we will often
 *       only have the ID. If the API is changed we could probably also eliminate
 *       the entityName parameter.
 */
export const useQueue = (entityName: string, queueId: string) => {
  const {queues, error, loading, refetch} = useQueues(entityName, true, true);
  if (loading || error) {
    return {error, loading, queue: undefined, refetch};
  }
  const queue = _.find(queues, {id: queueId});
  if (!queue) {
    // TODO: What is the correct way to specify error? GraphQLError?
    return {
      error: `No queue with id ${queueId} in entity ${entityName}`,
      loading: undefined,
      queue: undefined,
    };
  }
  return {
    queue,
    loading: undefined,
    error: undefined,
    refetch,
  };
};
